import * as React from "react";
import { useState, useId, forwardRef } from "react";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

const notificationVariants = cva(
  "relative flex items-start gap-3 rounded-lg p-2",
  {
    variants: {
      variant: {
        default: "bg-neutral-50",
        empty: "bg-neutral-50",
        success: "bg-green-50",
        info: "bg-blue-50",
        warning: "bg-yellow-50",
        error: "bg-red-50",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
);

type NotificationVariant = NonNullable<
  VariantProps<typeof notificationVariants>["variant"]
>;

interface NotificationProps {
  variant?: NotificationVariant;
  title: React.ReactNode;
  description?: React.ReactNode;
  className?: string;
  titleClassName?: string;
  /** Show a chevron toggle to expand/collapse the description. */
  expandable?: boolean;
  /** Initial expanded state when `expandable` is true. Defaults to `true`. */
  defaultExpanded?: boolean;
  /** When provided, shows a dismiss (×) button. */
  onDismiss?: () => void;
}

const accentVariants: Record<NotificationVariant, string> = {
  default: "bg-neutral-500",
  empty: "bg-neutral-500",
  success: "bg-green-600",
  info: "bg-blue-500",
  warning: "bg-yellow-600",
  error: "bg-red-500",
};

const iconVariants: Record<NotificationVariant, string> = {
  default: "fa-circle-info text-neutral-500",
  empty: "fa-circle-info text-neutral-500",
  success: "fa-circle-check text-green-600",
  info: "fa-circle-info text-blue-500",
  warning: "fa-triangle-exclamation text-yellow-600",
  error: "fa-circle-xmark text-red-500",
};

const actionButtonClass =
  "ml-auto flex size-5 shrink-0 items-center justify-center self-start rounded border-none bg-transparent p-0 text-base text-neutral-500 cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500";

const Notification = forwardRef<HTMLDivElement, NotificationProps>(
  (
    {
      variant = "default",
      title,
      description,
      className,
      titleClassName,
      expandable = false,
      defaultExpanded = true,
      onDismiss,
    },
    ref
  ) => {
    const descriptionId = useId();
    const [expanded, setExpanded] = useState(defaultExpanded);

    const role =
      variant === "warning" || variant === "error"
        ? "alert"
        : variant === "info" || variant === "success"
          ? "status"
          : undefined;

    const hasDescription = description != null;
    const showExpandToggle = expandable && hasDescription;
    const showDescription = hasDescription && (!expandable || expanded);
    const titleIconClassName = cn(
      "fa-regular flex size-5 shrink-0 items-center justify-center self-start text-base leading-none",
      iconVariants[variant]
    );

    return (
      <div
        ref={ref}
        role={role}
        className={cn(notificationVariants({ variant }), className)}
      >
        {/* Left accent line */}
        <div
          className={cn(
            "w-0.5 shrink-0 self-stretch rounded-[3px]",
            accentVariants[variant]
          )}
          aria-hidden="true"
        />

        {/* Content */}
        <div className="flex min-w-0 flex-1 flex-col gap-1 py-1">
          <div className="flex w-full items-center gap-2">
            <i className={titleIconClassName} aria-hidden="true" />
            <div
              className={cn(
                "m-0 flex-1 font-inter text-sm font-medium leading-5 text-neutral-800",
                titleClassName
              )}
            >
              {title}
            </div>
          </div>
          {showDescription && (
            <div
              id={showExpandToggle ? descriptionId : undefined}
              className="m-0 font-inter text-sm font-normal leading-5 text-neutral-500"
            >
              {description}
            </div>
          )}
        </div>

        {/* Expand/collapse toggle */}
        {showExpandToggle && (
          <button
            type="button"
            className={cn(actionButtonClass, "text-neutral-500")}
            onClick={() => setExpanded((prev) => !prev)}
            aria-expanded={expanded}
            aria-controls={expanded ? descriptionId : undefined}
            aria-label={expanded ? "Collapse details" : "Expand details"}
          >
            <i
              className={`fa-solid fa-chevron-${expanded ? "up" : "down"} leading-none`}
              aria-hidden="true"
            />
          </button>
        )}

        {/* Dismiss button */}
        {onDismiss && (
          <button
            type="button"
            className={actionButtonClass}
            onClick={onDismiss}
            aria-label="Dismiss notification"
          >
            <i className="fa-solid fa-xmark leading-none" aria-hidden="true" />
          </button>
        )}
      </div>
    );
  }
);
Notification.displayName = "Notification";

export { Notification, notificationVariants, type NotificationProps };
